<template>
  <RevCard class="flex-1 p-24">
    <RevTag
      v-if="!mainTaxResidenceCountry"
      class="mb-8"
      :label="i18n(translations.toDoLabel)"
      variant="warning"
    />

    <WithSkeletonLoader :is-loading="pending">
      <div class="flex items-baseline" data-test="main-country-header">
        <div class="grow">
          <h2 class="heading-2 mb-8">
            {{ i18n(translations.mainTitle) }}
          </h2>
          <div class="body-1 mb-4">
            {{ i18n(translations.mainDescription) }}
          </div>
        </div>
        <RevButtonIcon
          v-if="mainTaxResidenceCountry"
          data-test="edit-main-country-button"
          full-width="never"
          :icon="IconEdit"
          variant="secondary"
          @click="handleEditMainCountry"
        />
      </div>

      <div class="my-16">
        <div v-if="mainTaxResidenceCountry" class="flex items-center">
          <Flag
            class="mr-8"
            :country-code="mainTaxResidenceCountry"
            size="xl"
          />
          <div class="items-baseline">
            <span class="mr-8">
              {{ i18n.country(mainTaxResidenceCountry) }}
            </span>
          </div>
        </div>
        <div v-if="hasMissingTaxResidenceCountry">
          <RevButton
            full-width="never"
            variant="primary"
            @click="handleEditMainCountry"
          >
            {{ i18n(translations.addCountry) }}
          </RevButton>
        </div>

        <div
          v-if="mainTaxResidenceCountry || shouldDisplaySecondaryCountries"
          class="mt-24 flex items-baseline"
        >
          <div class="grow" data-test="additional-countries-header">
            <h3 class="heading-2 my-8">
              {{ i18n(translations.secondaryTitle) }}
            </h3>
            <div class="body-1 mb-16">
              {{ i18n(translations.secondaryDescription) }}
              <RevLink target="_blank" :to="i18n(translations.helpLinkUrl)">
                {{ i18n(translations.helpLinkText) }}
              </RevLink>
            </div>
          </div>
          <RevButtonIcon
            v-if="shouldDisplaySecondaryCountries"
            class="flex shrink"
            data-test="edit-additional-countries-button"
            full-width="never"
            :icon="IconEdit"
            variant="secondary"
            @click="handleEditAdditionalCountries"
          />
        </div>

        <ul
          v-if="shouldDisplaySecondaryCountries"
          class="mt-16"
          data-test="additional-countries-list"
        >
          <li
            v-for="additionalCountry in additionalTaxResidenceCountries"
            :key="additionalCountry"
            :aria-label="i18n.country(additionalCountry)"
            class="flex items-center"
          >
            <Flag
              class="my-8 mr-8"
              :country-code="additionalCountry"
              size="xl"
            />
            <div class="items-baseline">
              <span class="mr-8">
                {{ i18n.country(additionalCountry) }}
              </span>
            </div>
          </li>
        </ul>
        <RevButton
          v-if="!shouldDisplaySecondaryCountries && mainTaxResidenceCountry"
          class="mt-16"
          full-width="never"
          variant="secondary"
          @click="handleEditAdditionalCountries"
        >
          {{ i18n(translations.addEstablishmentsButton) }}
        </RevButton>
      </div>
    </WithSkeletonLoader>
  </RevCard>
  <MainTaxResidenceCountryModal
    :additional-countries="additionalTaxResidenceCountries"
    :main-country="mainTaxResidenceCountry"
    :tax-residence-country-options
    @main-country-updated="refresh"
  />
  <AdditionalTaxResidenceCountriesModal
    :additional-countries="additionalTaxResidenceCountries"
    :main-country="mainTaxResidenceCountry"
    :tax-residence-country-options
    @additional-countries-updated="refresh"
  />
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed } from 'vue'

import { type Country, sellerProfileAPI } from '@backmarket/http-api'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { openModal } from '@ds/components/ModalBase'
import { RevTag } from '@ds/components/Tag'
import { IconEdit } from '@ds/icons/IconEdit'

import { MODAL_NAMES } from '../../../../Profile.constants'
import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './TaxResidenceCountry.translations'
import AdditionalTaxResidenceCountriesModal from './components/AdditionalCountriesModal/AdditionalCountriesModal.vue'
import MainTaxResidenceCountryModal from './components/MainCountryModal/MainCountryModal.vue'

const MAIN_RESIDENCE_MODAL_NAME = MODAL_NAMES.EDIT_TAX_MAIN_RESIDENCE_COUNTRY
const ADDITIONAL_RESIDENCE_MODAL_NAME =
  MODAL_NAMES.EDIT_TAX_ADDITIONAL_RESIDENCE_COUNTRIES

const i18n = useI18n()
const { openErrorToast } = useTheToast()

defineProps<{
  taxResidenceCountryOptions: Country[] | undefined
}>()

const {
  data: taxResidencePayload,
  error,
  pending,
  refresh,
} = await useHttpLazyFetch(sellerProfileAPI.getTaxResidenceCountries)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const additionalTaxResidenceCountries = computed(() => {
  return taxResidencePayload?.value?.otherResidenceCountries ?? []
})

const mainTaxResidenceCountry = computed(() => {
  return taxResidencePayload?.value?.mainResidenceCountry
})

const hasMissingTaxResidenceCountry = computed(() => {
  return mainTaxResidenceCountry.value === null
})

const shouldDisplaySecondaryCountries = computed(() => {
  return additionalTaxResidenceCountries?.value.length > 0
})

function handleEditMainCountry() {
  openModal(MAIN_RESIDENCE_MODAL_NAME)
}

function handleEditAdditionalCountries() {
  openModal(ADDITIONAL_RESIDENCE_MODAL_NAME)
}
</script>
